import { keyMatchesChunk } from '@/i18n/registry';
import type { HubUiLocale, I18nChunkFile, I18nChunkId } from '@/i18n/types';

type ChunkImporter = () => Promise<I18nChunkFile>;

/** Static map so bundlers can resolve JSON chunk paths at build time. */
const CHUNK_IMPORTERS: Record<I18nChunkId, ChunkImporter> = {
  chromeNav: () =>
    import('@/i18n/chrome/nav.json').then((module) => module.default),
  chromeLegal: () =>
    import('@/i18n/chrome/legal.json').then((module) => module.default),
  chromeReports: () =>
    import('@/i18n/chrome/reports.json').then((module) => module.default),
  sharedCommon: () =>
    import('@/i18n/shared/common.json').then((module) => module.default),
  hubPage: () =>
    import('@/i18n/pages/hub/page.json').then((module) => module.default),
  hubDialogs: () =>
    import('@/i18n/pages/hub/dialogs.json').then((module) => module.default),
  profilePage: () =>
    import('@/i18n/pages/profile/page.json').then((module) => module.default),
  travelAuthShared: () =>
    import('@/i18n/pages/travel-authorization/shared.json').then(
      (module) => module.default
    ),
  travelPolicyPage: () =>
    import('@/i18n/pages/travel-authorization/policy.json').then(
      (module) => module.default
    ),
  authorizationFlowPage: () =>
    import('@/i18n/pages/travel-authorization/authorization-flow.json').then(
      (module) => module.default
    ),
  travelAuthRequests: () =>
    import('@/i18n/pages/travel-authorization/requests.json').then(
      (module) => module.default
    ),
  adminBroadcasts: () =>
    import('@/i18n/pages/administration/broadcasts.json').then(
      (module) => module.default
    ),
};

function resolveEntryValue(
  entry: I18nChunkFile['strings'][string],
  locale: HubUiLocale
): string | undefined {
  if (entry.doNotTranslate) {
    return entry.en;
  }
  const localized = entry[locale];
  if (localized == null || localized === '') {
    return undefined;
  }
  return localized;
}

export async function loadSurfaceChunk(
  chunkId: I18nChunkId,
  locale: HubUiLocale
): Promise<Record<string, string>> {
  const chunk = await CHUNK_IMPORTERS[chunkId]();
  const rawStrings = chunk?.strings;
  const strings =
    rawStrings && typeof rawStrings === 'object' && !Array.isArray(rawStrings)
      ? (rawStrings as Record<string, I18nChunkFile['strings'][string]>)
      : {};
  const bundle: Record<string, string> = {};

  for (const [key, entry] of Object.entries(strings)) {
    if (entry == null || typeof entry !== 'object') {
      continue;
    }
    if (!keyMatchesChunk(key, chunkId)) {
      continue;
    }
    const value = resolveEntryValue(entry, locale);
    if (value != null) {
      bundle[key] = value;
    }
  }

  return bundle;
}
